import React, { useEffect, useState, useRef } from 'react';
import AppRoutes from "./router/AppRoutes";
import { MobileContainer } from './containers/MobileContainer';
import { Modals } from './components/Modals';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { SkeletonTheme } from 'react-loading-skeleton';
import { storeAlreadyBeen } from './utils/storeAlreadyBeen';
import useToken from './hooks/useToken';
import { setValidToken } from './features/login/loginSlice';
import { getHasProfile, getProfile } from './features/profile/profileSlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { BottomNav } from './components/BottomNav';
import { deleteCookie, getCookie, isCookieExpired, setCookie } from './utils/CookieUtil';
import { addToStorage} from './utils/LocalStorageExplorer';
import { setPinCodeAccepted } from './features/access/accessSlice';
import { registerPush } from './features/notifications/notifications';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { handleAboutModal, handleProfileEditModal, handleQRScannerModal, handleAnalysisInfoModal, handleOrderInfoModal, handlePatientOrderInfoModal, handleBonusesBottomSheet, handleBonusesModal, handleOrdersFinancesModal, handlePatientInfoModal, handlePatientsModal, handleTemplateSheetAnalysisInfoModal, handleTemplateSheetCreateModal, handleTemplateSheetModal } from './features/modals/modalsSlice';
import { getProducts } from './features/products/productSlice';
import { setZabor } from './features/categories/categoriesSlice';



const validPaths = ['/', '/help', '/profile'];

function App() {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const token = useToken()
    const modals = useAppSelector(state => state.modals)
    const location = useLocation()
    const { theme } = useAppSelector(state => state.settings)
    const { alreadyBeen, accepted } = useAppSelector(state => state.access)
    const { profile_got, has_profile, data, profile_id} = useAppSelector(state => state.profile)
    const { valid } = useAppSelector(state => state.login.token)
    const [someOpened, setSomeOpened] = useState(false)
    const dob = getCookie("dob")
    const zabor = useAppSelector(state => state.categories.zabor)

    const initialX = useRef<number | null>(null)
    const openedModal = Object.entries(modals).find(([_, modal]) => modal.opened);

     const closeModal = (modalKey: string) => {
        switch (modalKey) {
            case 'profileEditModal':
                dispatch(handleProfileEditModal()); // Explicitly set to false
                break;
            case 'aboutAppModal':
                dispatch(handleAboutModal());
                break;
            case 'qRScannerModal':
                dispatch(handleQRScannerModal());
                break;
            case 'patientOrderInfoModal':
                dispatch(handlePatientOrderInfoModal());
                break;
            case 'orderInfoModal':
                dispatch(handleOrderInfoModal());
                break;
            case 'analysisInfoModal':
                dispatch(handleAnalysisInfoModal());
                break;
            case 'bonusesBottomSheet': 
                dispatch(handleBonusesBottomSheet());
                break;
            case 'bonusesModal': 
                dispatch(handleBonusesModal());
                break;
            case 'ordersFinancesModal': 
                dispatch(handleOrdersFinancesModal());
                break;
            case 'patientInfoModal': 
                dispatch(handlePatientInfoModal());
                break;
            case 'patientsModal': 
                dispatch(handlePatientsModal());
                break;
            case 'templateSheetAnalysisInfoModal': 
                dispatch(handleTemplateSheetAnalysisInfoModal(null));
                break;
            case 'templateSheetCreateModal': 
                dispatch(handleTemplateSheetCreateModal());
                break;
            case 'templateSheetUserInfoModal': 
                dispatch(handleTemplateSheetModal());
                break;
            default:
                console.warn(`No handler defined for modal: ${modalKey}`);
                break;
        }

        return false
    }


    useEffect(() => {
        if(zabor.length === 0 && token)
        {
            dispatch(getProducts({id: 13, part: 0})).unwrap().then((response) => {
               dispatch(setZabor(response.analiz))
            })
        }
    }, [zabor])

    useEffect(() => {
            
        const handleTouchStart = (e: TouchEvent) => {
            initialX.current = e.touches[0].clientX;
        };

        const handleTouchMove = (e: TouchEvent) => {
            if (initialX.current === null) return;

            const currentX = e.touches[0].clientX;
            const diffX = currentX - initialX.current;

            if (diffX > 100) {

                if (openedModal) {
                    const [modalKey] = openedModal;
                    // Close only the currently opened modal
                    closeModal(modalKey)
                    // Prevent default back behavior when closing modals
                    initialX.current = null;
                    return;
                } else {
                    if (window.location.search !== "/") window.history.back();
                }

                // Default back behavior when no modals are open
                initialX.current = null;
                return;
            }
        };

        document.addEventListener('touchstart', handleTouchStart);
        document.addEventListener('touchmove', handleTouchMove);

        return () => {
            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
        };

    }, [modals, dispatch]);

        
    useEffect(() => {
         const backButtonHandler = ({ canGoBack }: { canGoBack: boolean }) => {
            // Check if any modal is open
            if (openedModal) {
            
                const [modalKey] = openedModal;

                // Close only the currently opened modal
                closeModal(modalKey)
            }else{
                if(window.location.search !== "/") window.history.back()
            }

            // Default back behavior when no modals are open
            return true;
            
        };
    
        const setupListener = async () => {
            const listener = await CapacitorApp.addListener('backButton', backButtonHandler);
            return () => listener.remove();
        };

        const cleanupPromise = setupListener();

        return () => {
            cleanupPromise.then(cleanup => cleanup())
        };
    }, [modals, dispatch])

    CapacitorApp.addListener('appUrlOpen', (data) => {
        const url = new URL(data.url);
        const discountCode = url.searchParams.get('discount'); // Получаем параметр
        if (discountCode) {
          // Передаем код купона в приложение (например, через состояние Redux, Context или localStorage)
          localStorage.setItem('discountCode', discountCode);
          
        }
      });


    useEffect(() => {
        dispatch(setValidToken(token as boolean))
    }, [dispatch, token])

    useEffect(() => {
        if(Capacitor.isNativePlatform() && profile_id !== 0) registerPush(profile_id)
    }, [profile_id])

    useEffect(() => {
        if (accepted.valid) {
            setCookie("accepted", { valid: true }, 1)
        }
    }, [accepted.valid])

    useEffect(() => {
        if (valid) {
            dispatch(getHasProfile({dob: dob}))
        }
    }, [dispatch, dob, valid])

    useEffect(() => {
        const modalsKeys = Object.keys(modals)
        const some = modalsKeys.some(key => {
            const typedKey = key as keyof typeof modals
            return modals[typedKey].opened
        })
        setSomeOpened(some)
    }, [modals])

    useEffect(() => {
        storeAlreadyBeen(alreadyBeen.valid)
    }, [alreadyBeen.valid])

    useEffect(() => {
        addToStorage("theme", theme.dark)
        if (!theme.dark) {
            document.body.style.backgroundColor = "#F4FAFA"
            return
        }
        document.body.style.backgroundColor = "#1C1C1E"
    }, [theme.dark])

    useEffect(() => {
        if (valid && accepted.valid && has_profile) {
            if (!profile_got) {
               
                dispatch(getProfile())
            }
        }

    }, [dispatch, dob, profile_got, valid, accepted.valid, has_profile])

    useEffect(() => {
        if (!getCookie("accepted")) {
            dispatch(setPinCodeAccepted(false))
            return
        }
        if (isCookieExpired("accepted")) {
            deleteCookie("accepted")
            dispatch(setPinCodeAccepted(false))
            return
        }
        dispatch(setPinCodeAccepted(true))
        
    }, [dispatch])

    return (
         <SkeletonTheme baseColor="lightgray" highlightColor="#fff">
            <MobileContainer>
                <div className={`App p-rel m-100v pageScrollable ${!someOpened ? "scrollEnabled" : "pageZoomOut scrollDisabled"}`}>
                    <AppRoutes />
                </div>
                <Modals />
                {
                    !validPaths.includes(location.pathname) ? null : <BottomNav current={validPaths.findIndex(item => item === location.pathname) + 1} />
                }
            </MobileContainer>

        </SkeletonTheme>
    );
}

export default App;
